import React from 'react'

import spacetime from 'spacetime'

import { DriftUserIF } from '@driftt/app-config/dist/utils/driftTypes'
import { Box, Card, Flex, Link, StatusBadge, Text, Tooltip, UserAvatar } from '@driftt/dds'

import { useAppSelector } from 'hooks/useRedux'
import { ActivityEndUser, ActivityEvent, ActivityType } from 'modules/accountActivity/domain'
import { getAgentDisplayName, getMeetingDateStr, getMeetingTimeStr } from 'utils/activity'
import { getEndUserDisplayName } from 'utils/endUser'
import { pluralize } from 'utils/formatStrings'
import { contactPath } from 'utils/paths'

import { ActivityIcon } from './ActivityIcon'

interface MeetingActivityProps {
  activityEvent: ActivityEvent
  presentEndUserIds: Set<number>
  trackAction: (action: string, additionalData?: any) => void
}

const AttendeeDisplay = ({
  name,
  avatarUrl,
  isAvailable
}: {
  name: string
  avatarUrl: string
  isAvailable?: boolean
}) => (
  <Flex alignItems="center">
    <UserAvatar
      imageUrl={avatarUrl}
      name={name}
      size="x-small"
      status={isAvailable !== undefined ? (isAvailable ? 'available' : 'away') : undefined}
    />
    {name && (
      <Text variant="bodyBold" truncate css={{ maxWidth: '132px' }}>
        {name}
      </Text>
    )}
  </Flex>
)

const AttendeeInfoTooltip = ({
  attendees,
  isEndUsers = false,
  presentEndUserIds
}: {
  attendees: DriftUserIF[] | ActivityEndUser[]
  isEndUsers?: boolean
  presentEndUserIds?: Set<number>
}) => {
  return (
    <Flex direction="column" css={{ padding: '8px 4px' }}>
      {attendees.map(attendee => {
        const attendeeName = isEndUsers
          ? getEndUserDisplayName(attendee as ActivityEndUser)
          : getAgentDisplayName(attendee as DriftUserIF)
        return isEndUsers ? (
          <Link key={attendee.id} href={contactPath(attendee.id)} target="_blank">
            <AttendeeDisplay
              name={attendeeName}
              avatarUrl={attendee.avatarUrl}
              isAvailable={presentEndUserIds?.has(attendee.id)}
            />
          </Link>
        ) : (
          <AttendeeDisplay key={attendee.id} name={attendeeName} avatarUrl={attendee.avatarUrl} />
        )
      })}
    </Flex>
  )
}

const AttendeeInfo = ({
  attendees,
  isEndUsers = false,
  presentEndUserIds,
  trackAction
}: {
  attendees: DriftUserIF[] | ActivityEndUser[]
  isEndUsers?: boolean
  presentEndUserIds?: Set<number>,
  trackAction: (action: string, additionalData?: any) => void
}) => {
  const mainAttendee = attendees[0]
  const otherAttendees = attendees.slice(1)
  const attendeeName = isEndUsers
    ? getEndUserDisplayName(mainAttendee as ActivityEndUser)
    : getAgentDisplayName(mainAttendee as DriftUserIF)
  return (
    <Flex alignItems="center">
      {isEndUsers ? (
        <Link
          href={contactPath(mainAttendee?.id)}
          onClick={() => trackAction('Meeting attendee contact click')}
          target="_blank">
          <AttendeeDisplay
            name={attendeeName}
            avatarUrl={mainAttendee?.avatarUrl}
            isAvailable={presentEndUserIds?.has(mainAttendee.id)}
          />
        </Link>
      ) : (
        <AttendeeDisplay name={attendeeName} avatarUrl={mainAttendee?.avatarUrl} />
      )}
      {!!otherAttendees && otherAttendees.length > 0 && (
        <Tooltip
          content={<AttendeeInfoTooltip attendees={otherAttendees} isEndUsers={isEndUsers} />}>
          <Text variant="metadata">{`+ ${otherAttendees.length} ${pluralize(
            'other',
            otherAttendees.length
          )}`}</Text>
        </Tooltip>
      )}
    </Flex>
  )
}

export const MeetingActivity = ({ activityEvent, presentEndUserIds, trackAction }: MeetingActivityProps) => {
  const timestamp = spacetime(activityEvent.createdAt).format('{hour}:{minute-pad} {AMPM}')
  const isRecordingEvent = activityEvent.activityType === ActivityType.MEETING_RECORDED
  const { agentParticipants, endUserParticipantObjects, videoEmbedLink } = activityEvent.activityContext

  const { meetingType, slotStart } =
    isRecordingEvent && activityEvent.activityContext.schedulingEvent
      ? activityEvent.activityContext.schedulingEvent
      : activityEvent.activityContext

  const orgUsersById = useAppSelector(({ orgUsersById }) => orgUsersById.users)
  const agentAttendees = agentParticipants
    ?.map(id => orgUsersById[id])
    .filter(participant => !!participant) as DriftUserIF[]

  const meetingDate = getMeetingDateStr(slotStart)
  const meetingTime = getMeetingTimeStr(slotStart)

  const meetingEventType = () => {
    switch (activityEvent.activityType) {
      case ActivityType.MEETING_BOOKED:
        return 'Booked'
      case ActivityType.MEETING_CANCELED:
        return 'Cancelled'
      case ActivityType.MEETING_RESCHEDULED:
        return 'Rescheduled'
      case ActivityType.MEETING_RECORDED:
        return 'Recorded'
    }
  }

  return (
    <Box
      key={activityEvent.createdAt}
      css={{
        position: 'relative',
        zIndex: 1
      }}>
      <Text variant="metadata" css={{ position: 'relative', top: '-8px' }}>
        {timestamp}
      </Text>
      {/* event card */}
      <Card
        css={{
          width: '270px',
          padding: '16px',
          marginBottom: '24px',
          position: 'relative'
        }}>
        <Card.Content>
          <Flex direction="column" gap={0}>
            <Flex
              gap={0}
              direction="column"
              fullWidth
              css={
                isRecordingEvent ? {} : { borderBottom: '1px solid $gray3', paddingBottom: '16px' }
              }>
              <Flex justifyContent="space-between" fullWidth>
                {isRecordingEvent && videoEmbedLink ? (
                  <Link
                    href={videoEmbedLink}
                    target="_blank"
                    onClick={() => trackAction('Meeting recording clicked')}>
                    <Text variant="bodyBold">{meetingType}</Text>
                  </Link>
                ) : (
                  <Text variant="bodyBold">{meetingType}</Text>
                )}
                <StatusBadge variant={'inactive'} css={{ marginTop: '2px' }}>
                  {meetingEventType()}
                </StatusBadge>
              </Flex>
              <Text variant="metadata">{meetingDate}</Text>
              <Text variant="metadata">{meetingTime}</Text>
            </Flex>
            {!isRecordingEvent && (
              <Flex direction="column" css={{ paddingTop: '16px' }}>
                {agentAttendees && (
                  <AttendeeInfo attendees={agentAttendees} trackAction={trackAction} />
                )}
                {endUserParticipantObjects && (
                  <AttendeeInfo
                    attendees={endUserParticipantObjects}
                    presentEndUserIds={presentEndUserIds}
                    trackAction={trackAction}
                    isEndUsers
                  />
                )}
              </Flex>
            )}
          </Flex>
        </Card.Content>
      </Card>
      {/* center icon on timeline */}
      <Flex
        fullWidth
        justifyContent="center"
        css={{
          position: 'relative',
          top: 'calc(-100% + 32px)'
        }}>
        <ActivityIcon activityType={activityEvent.activityType} />
      </Flex>
    </Box>
  )
}
